import React from "react"

import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@coval/ui"
import { Trash2 } from "lucide-react"

interface DeleteDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Runs the delete. The dialog does not close itself — the caller closes it. */
  onConfirm: () => void
  /** Dialog heading, e.g. "Delete Metric". */
  title: string
  /** Bold confirmation prompt, e.g. "Are you sure you want to delete this metric?". */
  prompt: React.ReactNode
  /**
   * Recoverability note rendered on its own line below the prompt. Omit for a
   * delete with no recovery path.
   */
  note?: React.ReactNode
  /** Blocks dismissal and shows the confirm button's loading state. */
  isDeleting?: boolean
  /** Confirm button label; defaults to "Delete". */
  confirmLabel?: string
  /** data-testid for the confirm button. */
  confirmTestId?: string
}

/**
 * Shared confirmation dialog for deleting a resource: a bold prompt, an optional
 * recoverability note on its own line below the prompt, and a neutral primary
 * Delete button paired with a secondary Cancel. Presentational — the caller
 * owns the delete call and closes the dialog on success.
 */
export function DeleteDialog({
  open,
  onOpenChange,
  onConfirm,
  title,
  prompt,
  note,
  isDeleting = false,
  confirmLabel = "Delete",
  confirmTestId,
}: DeleteDialogProps) {
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!isDeleting) {
          onOpenChange(next)
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <p className="font-medium text-coval-text">{prompt}</p>
          {note ? (
            <p className="mt-3 mb-2 text-xs text-coval-text-muted">{note}</p>
          ) : null}
        </DialogBody>
        <DialogFooter>
          <Button
            variant="secondary"
            onClick={() => onOpenChange(false)}
            disabled={isDeleting}
            aria-label="Cancel delete"
          >
            Cancel
          </Button>
          <Button
            variant="default"
            onClick={onConfirm}
            loading={isDeleting}
            data-testid={confirmTestId}
            aria-label="Confirm delete"
          >
            {!isDeleting && <Trash2 className="h-4 w-4" />}
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
